import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from '@features/auth/context/AuthContext';
import { PlaylistProvider } from '@features/playlists/context/PlaylistContext';
import LoadingSpinner from '@shared/components/LoadingSpinner';
import { App as CapacitorApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { supabase } from '@/lib/supabase';
import { Capacitor } from '@capacitor/core';

// ==========================================
// Critical Routes - 즉시 로드 (Initial Bundle)
// ==========================================
import MainMenuPage from '@/pages/MainMenuPage';

// ==========================================
// Non-Critical Routes - 지연 로드 (Lazy Load)
// ==========================================

// 찬송가 목록 페이지
const HomePage = lazy(
  () => import('@features/hymns/pages/HomePage')
);

// 찬송가 상세 페이지 (사용자가 클릭할 때 로드)
const HymnDetailPage = lazy(
  () => import('@features/hymns/pages/HymnDetailPage')
);

// 성경 페이지들
const BiblePage = lazy(
  () => import('@features/bible/pages/BiblePage')
);
const BibleBookPage = lazy(
  () => import('@features/bible/pages/BibleBookPage')
);
const BibleChapterPage = lazy(
  () => import('@features/bible/pages/BibleChapterPage')
);

// 플레이리스트 페이지 (사용자가 클릭할 때 로드)
const PlaylistPage = lazy(
  () => import('@features/playlists/pages/PlaylistPage')
);

// ==========================================
// Admin Routes - 관리자만 로드 (Lazy Load)
// ==========================================

// 관리자 라우트 컴포넌트
const AdminRoute = lazy(
  () => import('@features/admin/components/AdminRoute')
);

// 관리자 페이지들 (일반 사용자는 절대 로드하지 않음)
const AdminDashboard = lazy(
  () => import('@features/admin/pages/AdminDashboard')
);
const HymnManagementPage = lazy(
  () => import('@features/admin/pages/HymnManagementPage')
);
const KeywordManagementPage = lazy(
  () => import('@features/admin/pages/KeywordManagementPage')
);
const UserManagementPage = lazy(
  () => import('@features/admin/pages/UserManagementPage')
);
const ForbiddenPage = lazy(
  () => import('@features/admin/pages/ForbiddenPage')
);
const BibleHymnRefPage = lazy(
  () => import('@features/admin/pages/BibleHymnRefPage')
);

function App() {
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      // Handle Android back button
      CapacitorApp.addListener('backButton', ({ canGoBack }) => {
        if (canGoBack) {
          window.history.back();
        } else {
          CapacitorApp.exitApp();
        }
      });

      // Handle deep links for OAuth callback
      CapacitorApp.addListener('appUrlOpen', async ({ url }) => {
        console.log('Deep link received:', url);

        // Handle OAuth callback
        if (url.includes('oauth/callback')) {
          try {
            // Close the browser that was opened for OAuth
            await Browser.close();
          } catch {
            // Browser might already be closed
          }

          try {
            // PKCE flow: Extract authorization code from query parameters
            const codeMatch = url.match(/[?&]code=([^&#]+)/);
            if (codeMatch) {
              const code = decodeURIComponent(codeMatch[1]);
              console.log('PKCE code received, exchanging for session...');
              const { error } = await supabase.auth.exchangeCodeForSession(code);
              if (error) {
                console.error('Error exchanging code for session:', error);
              } else {
                console.log('OAuth session set successfully via PKCE');
              }
              return;
            }

            // Implicit flow fallback: Extract tokens from hash fragment
            const hashIndex = url.indexOf('#');
            if (hashIndex !== -1) {
              const hash = url.substring(hashIndex + 1);
              const params = new URLSearchParams(hash);
              const access_token = params.get('access_token');
              const refresh_token = params.get('refresh_token');

              if (access_token && refresh_token) {
                const { error } = await supabase.auth.setSession({
                  access_token,
                  refresh_token,
                });
                if (error) {
                  console.error('Error setting session:', error);
                } else {
                  console.log('OAuth session set successfully via implicit flow');
                }
              }
            }
          } catch (error) {
            console.error('Error processing deep link:', error);
          }
        }
      });
    }

    return () => {
      if (Capacitor.isNativePlatform()) {
        CapacitorApp.removeAllListeners();
      }
    };
  }, []);

  return (
    <AuthProvider>
      <PlaylistProvider>
        <Router>
          <Suspense fallback={<LoadingSpinner message="페이지 로딩 중..." />}>
            <Routes>
              {/* 메인 메뉴 (로그인 + 찬송가/성경 선택) */}
              <Route path="/" element={<MainMenuPage />} />

              {/* 찬송가 Routes */}
              <Route path="/hymns" element={<HomePage />} />
              <Route path="/hymn/:id" element={<HymnDetailPage />} />
              <Route path="/playlist" element={<PlaylistPage />} />
              <Route path="/403" element={<ForbiddenPage />} />

              {/* 성경 Routes */}
              <Route path="/bible" element={<BiblePage />} />
              <Route path="/bible/:bookCode" element={<BibleBookPage />} />
              <Route path="/bible/:bookCode/:chapter" element={<BibleChapterPage />} />

              {/* Admin Routes - Lazy Loaded */}
              <Route
                path="/admin"
                element={
                  <AdminRoute>
                    <AdminDashboard />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/hymns"
                element={
                  <AdminRoute>
                    <HymnManagementPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/keywords"
                element={
                  <AdminRoute>
                    <KeywordManagementPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <AdminRoute>
                    <UserManagementPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/bible-hymns"
                element={
                  <AdminRoute>
                    <BibleHymnRefPage />
                  </AdminRoute>
                }
              />
            </Routes>
          </Suspense>
        </Router>
      </PlaylistProvider>
    </AuthProvider>
  );
}

export default App;
